import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

import { chatVisitor } from '~/lib/chat-visitor';

const FIELD =
  'w-full rounded-md border border-hairline-strong bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-3 focus:border-accent';
const LABEL = 'mb-1 block text-sm font-medium text-ink-2';

export interface ChatFallbackFormProps {
  /** Naam alvast invullen als we die al kennen uit een verlopen gesprek. */
  initialName?: string;
  busy?: boolean;
  error?: string;
}

/**
 * Het vangnet: staat de chat uit, is het buiten kantooruren, is er niemand vrij
 * of liep de wachttijd af — dan komt de bezoeker hier uit. De widget is nooit
 * een doodlopende weg; dat is precies wat hem beter maakt dan de `mailto:` die
 * er eerst stond.
 */
export function ChatFallbackForm({ initialName = '', busy, error }: ChatFallbackFormProps) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // De honeypot. Staat wél in de DOM (anders vult een bot hem niet in) maar is
  // onbereikbaar voor mens én schermlezer.
  const [website, setWebsite] = useState('');

  return (
    <form
      className="flex flex-col gap-3 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void chatVisitor.sendFallback({ name, email, message, website });
      }}
    >
      <p className="text-sm text-ink-2">
        Laat je gegevens achter, dan reageren we zo snel mogelijk per e-mail.
      </p>

      <div>
        <label className={LABEL} htmlFor="kb-chat-name">Je naam</label>
        <input id="kb-chat-name" className={FIELD} value={name} required maxLength={60}
               autoComplete="name" onChange={(e) => setName(e.target.value)} />
      </div>

      <div>
        <label className={LABEL} htmlFor="kb-chat-email">Je e-mailadres</label>
        <input id="kb-chat-email" className={FIELD} value={email} required type="email"
               autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
      </div>

      <div>
        <label className={LABEL} htmlFor="kb-chat-message">Je vraag</label>
        <textarea id="kb-chat-message" className={`${FIELD} min-h-24 resize-y`} value={message}
                  required maxLength={4000} onChange={(e) => setMessage(e.target.value)} />
      </div>

      {/* Honeypot: verborgen voor mensen (ook voor schermlezers, vandaar
          aria-hidden + tabIndex -1), zichtbaar voor een bot die alles invult.
          Wordt hij ingevuld, dan geeft de server SUCCES terug zonder te
          versturen — een bot mag nooit horen dat hij herkend is. */}
      <div className="hidden" aria-hidden>
        <label htmlFor="kb-chat-website">Website</label>
        <input id="kb-chat-website" name="website" tabIndex={-1} autoComplete="off"
               value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="rounded-md bg-accent px-3 py-2 text-base font-semibold text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow disabled:opacity-50"
      >
        {busy ? 'Versturen…' : 'Bericht versturen'}
      </button>
    </form>
  );
}

/** Bevestiging na het versturen. Een eigen toestand en geen boolean-vlag, zodat
 *  het paneel niet half formulier half bevestiging is. */
export function ChatFallbackSent() {
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <CheckCircle2 size={32} className="text-success" aria-hidden />
      <p className="text-lg font-semibold text-ink">Bericht verstuurd</p>
      <p className="text-base text-ink-2">
        We hebben je vraag binnen. Je krijgt zo snel mogelijk antwoord per e-mail.
      </p>
    </div>
  );
}
